import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { Order, OrderItem } from '../types';
import { OrderCard } from '../components/OrderCard';
import toast from 'react-hot-toast';
import { usePageScroll } from '../hooks/usePageScroll';

type OrderStatus = 'all' | 'pending' | 'confirmed' | 'completed' | 'cancelled';

export const Orders: React.FC = () => {
    usePageScroll('orders');

    const navigate = useNavigate();
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<OrderStatus>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [userId, setUserId] = useState<string | null>(null);
    const needsRefresh = useRef(false);
    const isMounted = useRef(true);

    // Загружаем сессию при монтировании
    useEffect(() => {
        const loadSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.user) {
                setUserId(session.user.id);
            }
            setLoading(false);
        };
        loadSession();

        return () => {
            isMounted.current = false;
        };
    }, []);

    const fetchOrders = useCallback(async () => {
        if (!userId) return;

        try {
            setLoading(true);
            const { data: ordersData, error: ordersError } = await supabase
                .from('customer_orders')
                .select(`
                    *,
                    items:customer_order_items(
                        *,
                        product:products(*),
                        variant:product_variants(*)
                    )
                `)
                .eq('customer_id', userId)
                .order('created_at', { ascending: false });

            if (ordersError) throw ordersError;

            if (isMounted.current) {
                setOrders(ordersData || []);
                needsRefresh.current = false;
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
            if (isMounted.current) {
                toast.error('Ошибка загрузки заказов');
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    }, [userId]);

    useEffect(() => {
        if (userId) {
            fetchOrders();
        }
    }, [userId, fetchOrders]);

    const refreshOrders = () => {
        needsRefresh.current = true;
        fetchOrders();
    };

    const filteredOrders = orders.filter(order => {
        if (statusFilter !== 'all' && order.status !== statusFilter) {
            return false;
        }
        if (searchQuery && !order.order_number.toLowerCase().includes(searchQuery.toLowerCase())) {
            return false;
        }
        return true;
    });

    const statusCounts = {
        all: orders.length,
        pending: orders.filter(o => o.status === 'pending').length,
        confirmed: orders.filter(o => o.status === 'confirmed').length,
        completed: orders.filter(o => o.status === 'completed').length,
        cancelled: orders.filter(o => o.status === 'cancelled').length,
    };

    if (loading) {
        return (
            <div className="loader-container">
                <div className="spinner"></div>
                <p>Загрузка заказов...</p>
            </div>
        );
    }

    if (!userId) {
        return (
            <div className="empty-state">
                <div className="empty-state-icon">📦</div>
                <h3>Войдите в аккаунт</h3>
                <p>Чтобы просмотреть свои заказы, войдите в аккаунт</p>
                <button onClick={() => navigate('/login')} className="auth-button">
                    Войти
                </button>
            </div>
        );
    }

    return (
        <div className="orders-page">
            <div className="orders-header">
                <div className="orders-container">
                    <div className="orders-title">
                        <button onClick={() => navigate(-1)} className="back-btn">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M15 18l-6-6 6-6" />
                            </svg>
                        </button>
                        <h1>Мои заказы</h1>
                    </div>

                    <div className="search-box">
                        <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                            type="text"
                            placeholder="Поиск по номеру заказа..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search-input"
                        />
                    </div>

                    <div className="filters">
                        {(['all', 'pending', 'confirmed', 'completed', 'cancelled'] as OrderStatus[]).map((status) => (
                            <button
                                key={status}
                                onClick={() => setStatusFilter(status)}
                                className={`filter-btn ${statusFilter === status ? 'active' : ''}`}
                            >
                                {status === 'all' ? 'Все' :
                                    status === 'pending' ? 'Ожидают' :
                                        status === 'confirmed' ? 'Подтверждены' :
                                            status === 'completed' ? 'Завершены' : 'Отменены'}
                                <span className="filter-count">({statusCounts[status]})</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="orders-container">
                {filteredOrders.length === 0 ? (
                    <div className="empty-orders">
                        <div className="empty-icon">📦</div>
                        <h3>
                            {searchQuery || statusFilter !== 'all'
                                ? 'Заказы не найдены'
                                : 'У вас пока нет заказов'}
                        </h3>
                        <p>
                            {searchQuery || statusFilter !== 'all'
                                ? 'Попробуйте изменить параметры поиска'
                                : 'Перейдите в каталог и сделайте первый заказ'}
                        </p>
                        {!searchQuery && statusFilter === 'all' && (
                            <button onClick={() => navigate('/products')} className="auth-button">
                                Перейти в каталог
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="orders-list">
                        {filteredOrders.map((order) => (
                            <OrderCard key={order.id} order={order} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};